import { getData } from "Agentic Module/api/getData";
import { postData } from "Agentic Module/api/postData";
import { baseAgenticURL } from "Agentic Module/config";
import { buildHITLUrl, mapFiltersResponse, normalizeDispatchesListResponse } from "../emailTriaging/emailTriageApi";
import { deleteData } from "Agentic Module/api/deleteData";

export async function hitlEmailTableData({ page, pageSize, filters, sort } = {}) {
  console.log("hitlEmailTableData", { page, pageSize, filters, sort });
  const data = await getData(
    buildHITLUrl(baseAgenticURL, { page, pageSize, filters, sort }),
  );
  return normalizeDispatchesListResponse(data);
}

const INTERNAL_FILTER_KEY_TO_API_PARAM = {
  material_code: "item_code",
  material_desc: "item_description",
};

function filterKeyToApiParamKey(internalKey) {
  if (internalKey == null || internalKey === "") return internalKey;
  const k = String(internalKey).trim();
  const lookup = k.toLowerCase();
  return INTERNAL_FILTER_KEY_TO_API_PARAM[lookup] ?? INTERNAL_FILTER_KEY_TO_API_PARAM[k] ?? k;
}

/** Append filter selections as query params (same shape as dispatches `filters`). */
export function appendHitlParam(params, filters) {
  if (!filters || typeof filters !== "object") return;
  Object.entries(filters).forEach(([key, value]) => {
    const apiKey = filterKeyToApiParamKey(key);
    if (value == null) return;
    if (Array.isArray(value)) {
      value
        .filter((v) => v != null && String(v).trim() !== "")
        .forEach((v) => params.append(apiKey, String(v)));
      return;
    }
    const v = String(value).trim();
    if (v !== "") params.set(apiKey, v);
  });
}
export async function hitlGetFilters(filters) {
  const params = new URLSearchParams();
  appendHitlParam(params, filters);
  const qs = params.toString();
  const queryString = qs ? `?${qs}` : "";
  const url = `${baseAgenticURL}/api/v1/email-triage/hitl/filter-options${queryString}`;
  const data = await getData(url);
  return mapFiltersResponse(data);
}

export async function hitlGetReferenceData({
  site_code,
  vendor_code,
  material_code,
  po_number
} = {}) {
  const params = new URLSearchParams();

  if (site_code) params.set("site_code", String(site_code).trim());
  if (vendor_code) params.set("vendor_code", String(vendor_code).trim());
  if (material_code) params.set("material_code", String(material_code).trim());
  if (po_number) params.set("po_number", String(po_number).trim());

  const url = `${baseAgenticURL}/api/v1/hitl/reference-data?${params.toString()}`;
  return getData(url);
}

const HITL_SAVE_FIELD_MAP = {
  vendor: ["vendor", "vendor_name", "vendorName", "supplier_name", "supplierName"],
  vendor_code: ["vendor_code", "vendorCode", "supplier_code", "supplierCode"],
  site: ["site", "site_name", "site_cluster", "siteCluster", "siteName"],
  site_code: ["site_code", "siteCode", "site_unit_code", "unit_code"],
  material: ["material", "mat_name", "material_name", "material_group", "material_desc", "materialDescription"],
  material_code: ["material_code", "materialCode", "mat_code", "item_code"],
  mat_code: ["mat_code", "material_code", "materialCode", "item_code"],
  po_no: ["po_no", "poNumber", "po_number"],
  invoice_no: ["invoice_no", "invoiceNo", "invoice_number"],
  vehicle_no: ["vehicle_no", "vehicleNo", "vehicle_number"],
  dispatch_qty: ["dispatch_qty", "quantity", "qty"],
  stock_qty: ["stock_qty", "stockQty"],
  uom: ["uom", "unit", "UOM"],
  date_of_dispatch: ["date_of_dispatch", "dispatch_date", "dispatchDate"],
};

function pickFirstDefinedValue(row, keys) {
  for (const key of keys) {
    const value = row?.[key];
    if (value !== undefined && value !== null && String(value).trim() !== "") return value;
  }
  return undefined;
}

function buildHitlSaveRow(row) {
  const saveRow = {};

  Object.entries(HITL_SAVE_FIELD_MAP).forEach(([canonicalKey, aliases]) => {
    const value = pickFirstDefinedValue(row, aliases);
    if (value !== undefined) saveRow[canonicalKey] = value;
  });

  return saveRow;
}

export async function saveHitlRows(rows) {
  if (!Array.isArray(rows) || rows.length === 0) return null;

  const payload = rows.map((row) => {
    if (row?.__send_full_row) {
      const {
        __send_full_row,
        __original,
        __updated_fields,
        __originalRaw,
        ...fullRow
      } = row;
      return {
        ...fullRow,
        updated_fields: Array.isArray(fullRow.updated_fields)
          ? fullRow.updated_fields.filter((field) => field != null && String(field).trim() !== "")
          : [],
      };
    }

    const normalizedFields = buildHitlSaveRow(row);
    const explicitUpdatedFields = Array.isArray(row?.__updated_fields)
      ? row.__updated_fields.filter((field) => field != null && String(field).trim() !== "")
      : null;

    return {
      source: row?.source ?? "vendor",
      id: row?.id ?? row?.row_id ?? row?.dispatch_row_id ?? row?.line_id ?? null,
      email_message_id: row?.email_message_id ?? row?.message_id ?? row?.mail_id ?? null,
      updated_by: row?.updated_by ?? "user@example.com",
      updated_fields: explicitUpdatedFields ?? Object.keys(normalizedFields),
      ...normalizedFields,
    };
  });

  return postData(`${baseAgenticURL}/api/v1/save_hitl_rows`, { rows: payload });
}

/** GET /api/v1/email_history?vendor_name=… — history is scoped by vendor. */
export async function emailHITLGetEmailHistory(email_id) {
  const v = email_id != null ? String(email_id).trim() : "";
  const params = new URLSearchParams();

  if (v !== "") {
    params.set("email_message_id", v);
  }

  const qs = params.toString();
  const queryString = qs ? `?${qs}` : "";

  return getData(
    `${baseAgenticURL}/api/v1/email-triage/email-history${queryString}`,
  );
}

export async function emailHITLCompose(payload) {
  return postData(`${baseAgenticURL}/api/v1/email-triage/generate-draft`, payload);
}

export async function emailHITLSend(payload) {
  return postData(`${baseAgenticURL}/api/v1/email-triage/mail-action`, payload);
}
export async function emailHITLDeleteMail(emailId) {
  return deleteData(
    `${baseAgenticURL}/api/v1/email-triage/delete-mail/${emailId}`,
  );
}
